import { defineStore } from "pinia"
import { ref } from "vue"
import { decodeJwt } from "jose"
import Cookies from "js-cookie"
import { play } from "@voomp/play-back"
import axios from "axios"

import { useRouter } from "vue-router"
import { toast } from "@/events"
import { computed } from "vue"
import { auth } from "@/events/auth"
import { setAuthorization } from "@/services/Http"
import { paths } from "@/utils/route-paths"

import AuthService from "@/services/resources/AuthService"
import MemberService from "@/services/resources/MemberService"
import DOMPurify from "dompurify"
import { isEmpty } from "@/utils/object-helpers"

const serviceMember = MemberService.build()

const initial = {
  data: {
    member_id: null,
    site_id: null,
    role: null
  }
}

// --- Legacy auth state types ---
interface LegacyMe {
  current?: {
    currentMember?: {
      email?: string
      name?: string
      cellphone?: string
      picture?: string
    }
  }
  member?: unknown
}

export const useAuthStore = defineStore('auth', () => {
  const studentMode = ref(false)
  const orginalRole = ref<'student' | 'admin' | 'owner' | null>(null)

  const router = useRouter()

  const _initialToken = Cookies.get('auth_greennCourse')
  const _token = ref<string | undefined>(_initialToken)
  const data = ref<typeof initial.data>(decodeToken(_initialToken ?? ''))

  // --- Legacy auth state ---
  const me = ref<LegacyMe | null>(null)
  const feedback = ref<unknown>(null)
  const userCustomFields = ref<boolean>(String(Cookies.get("userCustomFields")) === "true")

  if (_initialToken) {
    play.auth.token(_initialToken)
    orginalRole.value = data.value.role
  }

  const authorization = computed({
    get: () => {
      return _token.value
    },
    set: (newToken) => {
      _token.value = newToken
      Cookies.set('auth_greennCourse', newToken)
      play.auth.token(newToken)
      data.value = decodeToken(newToken)
      orginalRole.value = data.value.role
      auth.emit('change', data.value)
    }
  })

  // --- Legacy getters as computed ---
  const isAuthenticated = computed(() => !!_token.value)
  const getMe = computed(() => me.value)
  const getFeedback = computed(() => feedback.value)
  const getIsSuper = computed(() => /@cogna|@voomp/.test(me.value?.current?.currentMember?.email ?? ''))

  function decodeToken(token: string) {
    try {
      if (!token || token === 'undefined' || token === 'null') {
        return initial.data
      } else {
        return decodeJwt(token) as typeof initial.data
      }
    } catch (_error) {
      return initial.data
    }
  }

  function logout() {
    authorization.value = null
    router.replace({ name: 'SignIn' })
  }

  function toggleStudentMode() {
    studentMode.value = !studentMode.value
    data.value.role = studentMode.value ? "student" : orginalRole.value;
  }

  function getStudentMode() {
    return studentMode.value
  }

  function getLoginSocialURL(provider: string, siteId: number) {
    return `${import.meta.env.VUE_APP_API_HOST}/v2/auth/social/${provider}/redirect?site_id=${siteId}`
  }

  async function SignInWithPassword(payload: { email: string, password: string, captcha: string }) {
    try {
      const response = await play.auth_creators.signIn({
        email: payload.email,
        password: payload.password,
        captcha: payload.captcha,
      })
      authorization.value = response.token
      if (!getSiteId()) {
        router.replace({ name: 'SignInSite' })
      } else {
        router.replace({ name: 'Index' })
      }
    } catch (error) {
      const axiosErr = error as { response?: { data?: { errorMessage?: string; type?: string } } }
      const errMsg = axiosErr?.response?.data?.errorMessage || ''
      if (errMsg.toLowerCase().includes('não autorizado') || errMsg.toLowerCase().includes('not authorized')) {
        toast.error(errMsg)
      } else {
        toast.exception(error)
      }
    }
  }

  async function SignInSite(payload: { siteId: number }) {
    try {
      const { redirect } = await play.auth.sign_in_site({
        site_id: payload.siteId,
      })
      window.location.replace(redirect)
    } catch (error) {
      toast.exception(error)
    }
  }

  function $reset() {
  }

  function getMemberId() {
    return data.value?.member_id
  }

  function getSiteId() {
    return data.value?.site_id
  }

  function getRole() {
    return data.value?.role
  }

  function getAuthId() {
    return `${getMemberId()}-${getSiteId()}:${getRole()}`
  }

  function isAdmin() {
    return ["admin","owner"].includes(data.value?.role)
  }

  function isOriginalAdmin() {
    return ["admin","owner"].includes(orginalRole.value)
  }

  function loginWithGoogle() {
    play.auth.login_social({
      provider: 'google',
    })
  }

  function loginWithMicrosoft() {
    play.auth.login_social({
      provider: 'microsoft',
    })
  }

  async function setPassword(payload: { token: string, email: string, password: string, captcha: string }) {
    try {
      const response = await play.auth_creators.setPassword({
        token: payload.token,
        email: payload.email,
        password: payload.password,
        captcha: payload.captcha,
      })
      authorization.value = response.token
      if (!getSiteId()) {
        return { name: 'SignInSite' }
      } else {
        return { name: 'Index' }
      }
    } catch (error) {
      toast.exception(error)
    }
  }

  async function retrieveToken(authorization_code: string) {
    try {
      const response = await play.auth.retrieve_token({
        authorization_code,
      })
      authorization.value = response.token
      if (!getSiteId()) {
        return { name: 'SignInSite' }
      } else {
        return { name: 'Index' }
      }
    } catch (error) {
      toast.exception(error)
    }
  }

  // --- Legacy auth actions (merged) ---

  async function actionGetUserLogged() {
    const response = await serviceMember.read("/meta")
    return response
  }

  function actionUpdateAvatar(avatar: string) {
    if (me.value?.current?.currentMember) {
      me.value = {
        ...me.value,
        current: {
          ...me.value.current,
          currentMember: {
            ...me.value.current.currentMember,
            picture: avatar,
          },
        },
      }
    }
    setTimeout(() => {
      location.reload()
    }, 500)
  }

  async function actionEditProfile(payload: Record<string, unknown>) {
    const response = await serviceMember.postID(payload)
    return response
  }

  async function actionFetchMe() {
    if (me.value && !isEmpty(me.value?.member)) return
    const response = await serviceMember.read("/me")
    me.value = response
    return response
  }

  function loginRequest(payload: { username: string; password: string; captcha: string }) {
    const service = AuthService.build()
    return new Promise((resolve, reject) => {
      service
        .login(payload.username, payload.password, payload.captcha)
        .then((resp: unknown) => resolve(resp))
        .catch((err: unknown) => {
          Cookies.remove("auth_greennCourse")
          reject(err)
        })
    })
  }

  function updateUserCustomFields() {
    userCustomFields.value = true
  }

  async function actionLogin(payload: {
    Auth: string
    role: string
    custom_fields_filled: boolean
    [key: string]: unknown
  }) {
    const userType = payload.role === "student" ? 1 : 0

    Cookies.remove("auth_greennCourse")
    Cookies.remove("userCustomFields")
    Cookies.remove("currentSite")
    Cookies.remove("themeClub")
    Cookies.remove("metasSite")
    Cookies.remove("certificateConfig")
    Cookies.remove("type_student")
    Cookies.set("type_student", String(userType))

    const cookieOptions = { expires: 365, path: "/" }
    Cookies.set("auth_greennCourse", payload.Auth, cookieOptions)
    Cookies.set("access_level", payload.role, cookieOptions)
    Cookies.set("userCustomFields", String(payload.custom_fields_filled), cookieOptions)

    // Update internal state
    play.auth.url(import.meta.env.VUE_APP_API_HOST)
    play.auth.token(payload.Auth)
    setAuthorization(payload.Auth)
    _token.value = payload.Auth
    data.value = decodeToken(payload.Auth)
    orginalRole.value = data.value.role
    userCustomFields.value = !!payload.custom_fields_filled
  }

  async function actionDeepNavigate(payload: {
    role: string
    subdomain?: string
    domains?: Array<{ domain: string }>
    course_id?: number
    siteId?: number
    Auth: string
    custom_fields_filled: boolean
    [key: string]: unknown
  }) {
    const oldSiteId = JSON.parse(Cookies.get("currentSite") || '{}').currentSite?.id
    const isLocal = window.location.href.startsWith("http://")
    const isSubDomain = window.location.href.startsWith(`https://${payload.subdomain}`)

    let newPath = '/home'
    if (payload.course_id) {
      newPath = paths.watch(payload.course_id) ?? '/home'
    }

    let origin: string | null = null

    if (!isLocal && payload.subdomain && !payload.domains?.length && !isSubDomain) {
      origin = `https://${payload.subdomain}.voompplay.${import.meta.env.VUE_URL_DOMAIN}`
    }

    if (!isLocal && payload.domains?.length) {
      const currentDomain = payload.domains.find((it) => it.domain === window.location.origin)
      const locatedDomain = currentDomain || payload.domains[0]
      origin = locatedDomain?.domain
    }

    if (!origin) {
      origin = `https://app.voompplay.${import.meta.env.VUE_URL_DOMAIN}`
    }

    if (isLocal) {
      origin = window.location.origin
    }

    const isSameOrigin = window.location.origin === origin
    const isSameSite = oldSiteId === payload.siteId

    if (isSameOrigin && (!isSameSite || !payload.siteId)) {
      await actionLogin(payload)
    }

    if (isSameOrigin) {
      router.push({ path: newPath })
    } else {
      const resp = await axios.post(import.meta.env.VUE_APP_API_HOST + `/member/auth`, {
        auth: { payload },
      })
      newPath = newPath + `?rdr=${resp.data.string}`
      window.location.replace(origin + newPath)
    }
  }

  async function blogSelected(payload: {
    Auth: string
    role: string
    custom_fields_filled: boolean
    subdomain?: string
    domains?: Array<{ domain: string }>
    course_id?: number | null
    [key: string]: unknown
  }) {
    const userType = payload.role === "student" ? 1 : 0

    Cookies.remove("auth_greennCourse")
    Cookies.remove("userCustomFields")
    Cookies.remove("currentSite")
    Cookies.remove("themeClub")
    Cookies.remove("metasSite")
    Cookies.remove("certificateConfig")
    Cookies.remove("type_student")
    Cookies.set("type_student", String(userType))

    const cookieOptions = { expires: 365, path: "/" }
    Cookies.set("auth_greennCourse", payload.Auth, cookieOptions)
    Cookies.set("access_level", payload.role, cookieOptions)
    Cookies.set("userCustomFields", String(payload.custom_fields_filled), cookieOptions)

    // Update internal state
    play.auth.url(import.meta.env.VUE_APP_API_HOST)
    play.auth.token(payload.Auth)
    setAuthorization(payload.Auth)
    _token.value = payload.Auth
    data.value = decodeToken(payload.Auth)
    orginalRole.value = data.value.role
    userCustomFields.value = !!payload.custom_fields_filled

    // Redirection logic based on environment and role
    if (import.meta.env.VUE_APP_TYPE === "DEV") {
      if (payload.course_id) {
        router.push({ path: paths.watch(payload.course_id) ?? '/home' })
      } else {
        router.push({ path: "/home" })
      }
    } else if (
      import.meta.env.VUE_APP_TYPE === "PROD" &&
      (payload.role === "owner" || payload.role === "admin")
    ) {
      const domainTo = window.location.host
      if (domainTo.search(`app.voompplay.${import.meta.env.VUE_URL_DOMAIN}`) === -1) {
        const resp = await axios.post(import.meta.env.VUE_APP_API_HOST + `/member/auth`, { auth: { payload } })
        const sanitized = DOMPurify.sanitize(resp.data.string)
        window.location.href = `https://app.voompplay.${import.meta.env.VUE_URL_DOMAIN}?rdr=${sanitized}`
      } else {
        if (payload.course_id != null) {
          const sanitized = DOMPurify.sanitize(paths.watch(payload.course_id) ?? '')
          window.location.href = `http://app.voompplay.${import.meta.env.VUE_URL_DOMAIN}${sanitized}`
        } else {
          window.location.href = `https://app.voompplay.${import.meta.env.VUE_URL_DOMAIN}/home`
        }
      }
    } else if (
      payload &&
      payload.domains &&
      import.meta.env.VUE_APP_TYPE === "PROD" &&
      (payload.domains as Array<{ domain: string }>).length === 0 &&
      payload.role === "student"
    ) {
      const regex = new RegExp(`(voompplay.${import.meta.env.VUE_URL_DOMAIN})`, "gi")
      const result = regex.exec(window.location.host)
      if (result === null || window.location.host === `app.voompplay.${import.meta.env.VUE_URL_DOMAIN}`) {
        const resp = await axios.post(import.meta.env.VUE_APP_API_HOST + `/member/auth`, { auth: { payload } })
        const sanitized = DOMPurify.sanitize(resp.data.string)
        window.location.href = `https://${payload.subdomain}.voompplay.${import.meta.env.VUE_URL_DOMAIN}?rdr=${sanitized}`
      } else {
        if (payload.course_id != null) {
          const sanitized = DOMPurify.sanitize(paths.watch(payload.course_id) ?? '')
          window.location.href = `https://${payload.subdomain}.voompplay.${import.meta.env.VUE_URL_DOMAIN}${sanitized}`
        } else {
          window.location.href = `https://${payload.subdomain}.voompplay.${import.meta.env.VUE_URL_DOMAIN}/home`
        }
      }
    } else if (
      import.meta.env.VUE_APP_TYPE === "PROD" &&
      payload.domains &&
      (payload.domains as Array<{ domain: string }>).length !== 0
    ) {
      const domains = payload.domains as Array<{ domain: string }>
      const matchingDomains = domains.filter((e) => e.domain === "https://" + window.location.host)

      if (matchingDomains.length === 0) {
        const resp = await axios.post(import.meta.env.VUE_APP_API_HOST + `/member/auth`, { auth: { payload } })
        const sanitized = DOMPurify.sanitize(resp.data.string)
        window.location.href = `${domains[0].domain}?rdr=${sanitized}`
      } else {
        if (payload.course_id != null) {
          const sanitized = DOMPurify.sanitize(paths.watch(payload.course_id) ?? '')
          window.location.href = `${matchingDomains[0].domain}${sanitized}`
        } else {
          window.location.href = `${matchingDomains[0].domain}/home`
        }
      }
    }
  }

  async function actionLoginFromRDR() {
    const queryString = window.location.search
    if (!queryString) return false

    const urlParams = new URLSearchParams(queryString)
    const params = urlParams.get("rdr")
    const type = urlParams.get("type")

    if (!params) return false

    window.history.replaceState({}, '', window.location.pathname)

    const url = import.meta.env.VUE_APP_API_HOST + `/member/auth/` + params + "?type=" + type
    const resp = await axios.get(url)
    await actionLogin(resp.data.payload)
    return true
  }

  function logoutRequest(payload?: { feedback?: unknown }) {
    Cookies.remove("auth_greennCourse")
    Cookies.remove("memberAuth")
    Cookies.remove("currentSite")
    Cookies.remove("themeClub")
    Cookies.remove("metasSite")
    Cookies.remove("certificateConfig")
    Cookies.remove("type_student")

    _token.value = undefined
    data.value = initial.data
    me.value = null
    feedback.value = payload?.feedback ?? null
  }

  return {
    data,
    authorization,
    getStudentMode,
    setPassword,
    SignInWithPassword,
    SignInSite,
    logout,
    $reset,
    getMemberId,
    getSiteId,
    getAuthId,
    getRole,
    isAdmin,
    isOriginalAdmin,
    toggleStudentMode,
    retrieveToken,
    getLoginSocialURL,
    loginWithGoogle,
    loginWithMicrosoft,
    // Legacy auth (merged)
    me,
    feedback,
    userCustomFields,
    isAuthenticated,
    getMe,
    getFeedback,
    getIsSuper,
    actionGetUserLogged,
    actionUpdateAvatar,
    actionEditProfile,
    actionFetchMe,
    loginRequest,
    updateUserCustomFields,
    actionLogin,
    actionDeepNavigate,
    blogSelected,
    actionLoginFromRDR,
    logoutRequest,
  }
})
